import { getBackendUrl } from '@/utils/apiUrl';
import React, { useState, useEffect } from 'react';
import { toast } from 'sonner';
import Layout from '../components/Layout';
import { 
  Users, Plus, Trash2, Edit, Shield, Mail, Key, 
  CheckCircle, XCircle, AlertCircle, UserPlus, Lock 
} from 'lucide-react';

const ROLES = {
  admin: {
    label: 'Administrator',
    description: 'Voller Zugriff auf alle Funktionen und Einstellungen',
    color: 'red',
    icon: Shield
  },
  manager: {
    label: 'Manager',
    description: 'Kann Fälle verwalten und Mitarbeiter überwachen',
    color: 'blue',
    icon: Users
  },
  employee: {
    label: 'Mitarbeiter',
    description: 'Kann Fälle erstellen und bearbeiten',
    color: 'green',
    icon: Users
  }
};

export const UserManagement = ({ user }) => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  
  const [newUser, setNewUser] = useState({
    name: '',
    email: '',
    password: '',
    role: 'employee'
  });

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${getBackendUrl()}/api/users`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setUsers(data);
      } else {
        toast.error('Fehler beim Laden der Benutzer');
      }
    } catch (error) {
      console.error('Error fetching users:', error);
      toast.error('Verbindungsfehler');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateUser = async (e) => {
    e.preventDefault();
    
    if (!newUser.name || !newUser.email || !newUser.password) {
      toast.error('Bitte füllen Sie alle Felder aus');
      return;
    }

    try {
      const response = await fetch(`${getBackendUrl()}/api/users`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify(newUser)
      });

      if (response.ok) {
        toast.success('Benutzer erfolgreich erstellt!');
        setShowCreateModal(false);
        setNewUser({ name: '', email: '', password: '', role: 'employee' });
        fetchUsers();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Fehler beim Erstellen des Benutzers');
      }
    } catch (error) {
      console.error('Error creating user:', error);
      toast.error('Verbindungsfehler');
    }
  };

  const handleUpdateUser = async (userId, updates) => {
    try {
      const response = await fetch(`${getBackendUrl()}/api/users/${userId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify(updates)
      });

      if (response.ok) {
        toast.success('Benutzer aktualisiert');
        fetchUsers();
        setShowEditModal(false);
        setSelectedUser(null);
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Fehler beim Aktualisieren');
      }
    } catch (error) {
      console.error('Error updating user:', error);
      toast.error('Verbindungsfehler');
    }
  };

  const handleDeleteUser = async (userId, userName) => {
    if (!window.confirm(`Möchten Sie den Benutzer "${userName}" wirklich löschen?`)) {
      return;
    }

    try {
      const response = await fetch(`${getBackendUrl()}/api/users/${userId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (response.ok) {
        toast.success('Benutzer gelöscht');
        fetchUsers();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Fehler beim Löschen');
      }
    } catch (error) {
      console.error('Error deleting user:', error);
      toast.error('Verbindungsfehler');
    }
  };

  const handleResetPassword = async (userId) => {
    const newPassword = prompt('Neues Passwort eingeben:');
    if (!newPassword || newPassword.length < 6) {
      toast.error('Passwort muss mindestens 6 Zeichen lang sein');
      return;
    }

    try {
      const response = await fetch(`${getBackendUrl()}/api/users/${userId}/reset-password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({ password: newPassword })
      });

      if (response.ok) {
        toast.success('Passwort erfolgreich zurückgesetzt');
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Fehler beim Zurücksetzen des Passworts');
      }
    } catch (error) {
      console.error('Error resetting password:', error);
      toast.error('Verbindungsfehler');
    }
  };

  const getRoleBadgeColor = (role) => {
    const colors = {
      admin: 'bg-red-100 text-red-800 border-red-200',
      manager: 'bg-blue-100 text-blue-800 border-blue-200',
      employee: 'bg-green-100 text-green-800 border-green-200'
    };
    return colors[role] || 'bg-gray-100 text-gray-800 border-gray-200';
  };

  // Check if current user is admin
  const isAdmin = user?.role === 'admin';

  if (!isAdmin) {
    return (
      <Layout user={user}>
        <div className="p-6">
          <div className="max-w-2xl mx-auto">
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6 text-center">
              <AlertCircle className="w-12 h-12 text-yellow-600 mx-auto mb-4" />
              <h2 className="text-xl font-bold text-gray-900 mb-2">Keine Berechtigung</h2>
              <p className="text-gray-600">Nur Administratoren haben Zugriff auf die Benutzerverwaltung.</p>
            </div>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout user={user}>
      <div className="p-6">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="mb-6">
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-3">
                  <Users className="w-8 h-8 text-blue-600" />
                  Benutzerverwaltung
                </h1>
                <p className="text-gray-600 mt-1">Verwalten Sie Mitarbeiter und deren Zugriffsrechte</p>
              </div>
              <button
                onClick={() => setShowCreateModal(true)}
                className="flex items-center gap-2 px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium shadow-lg"
                data-testid="create-user-button"
              >
                <UserPlus className="w-5 h-5" />
                Neuer Mitarbeiter
              </button>
            </div>
          </div>

          {/* Stats Cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
            <div className="bg-white rounded-lg shadow p-6 border border-gray-200">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Gesamt Benutzer</p>
                  <p className="text-3xl font-bold text-gray-900">{users.length}</p>
                </div>
                <Users className="w-12 h-12 text-blue-600 opacity-20" />
              </div>
            </div>
            <div className="bg-white rounded-lg shadow p-6 border border-gray-200">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Administratoren</p>
                  <p className="text-3xl font-bold text-gray-900">
                    {users.filter(u => u.role === 'admin').length}
                  </p>
                </div>
                <Shield className="w-12 h-12 text-red-600 opacity-20" />
              </div>
            </div>
            <div className="bg-white rounded-lg shadow p-6 border border-gray-200">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Aktive Benutzer</p>
                  <p className="text-3xl font-bold text-gray-900">
                    {users.filter(u => u.is_active !== false).length}
                  </p>
                </div>
                <CheckCircle className="w-12 h-12 text-green-600 opacity-20" />
              </div>
            </div>
          </div>

          {/* Users Table */}
          <div className="bg-white rounded-lg shadow border border-gray-200">
            <div className="p-6 border-b border-gray-200">
              <h2 className="text-xl font-bold text-gray-900">Alle Mitarbeiter</h2>
            </div>
            
            {loading ? (
              <div className="p-12 text-center">
                <div className="animate-spin rounded-full h-12 w-12 border-4 border-blue-200 border-t-blue-600 mx-auto"></div>
                <p className="mt-4 text-gray-600">Lade Benutzer...</p>
              </div>
            ) : users.length === 0 ? (
              <div className="p-12 text-center">
                <Users className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                <p className="text-gray-600">Keine Benutzer vorhanden</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50 border-b border-gray-200">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Benutzer
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        E-Mail
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Rolle
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Status
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Erstellt
                      </th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Aktionen
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {users.map((userItem) => (
                      <tr key={userItem._id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            <div className="flex-shrink-0 h-10 w-10 rounded-full bg-blue-100 flex items-center justify-center">
                              <span className="text-blue-600 font-semibold">
                                {userItem.name.charAt(0).toUpperCase()}
                              </span>
                            </div>
                            <div className="ml-4">
                              <div className="text-sm font-medium text-gray-900">{userItem.name}</div>
                              {userItem._id === user.id && (
                                <span className="text-xs text-blue-600">(Sie)</span>
                              )}
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center gap-2 text-sm text-gray-900">
                            <Mail className="w-4 h-4 text-gray-400" />
                            {userItem.email}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`px-3 py-1 inline-flex text-xs leading-5 font-semibold rounded-full border ${getRoleBadgeColor(userItem.role)}`}>
                            {ROLES[userItem.role]?.label || userItem.role}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          {userItem.is_active !== false ? (
                            <span className="flex items-center gap-2 text-green-600 text-sm">
                              <CheckCircle className="w-4 h-4" />
                              Aktiv
                            </span>
                          ) : (
                            <span className="flex items-center gap-2 text-red-600 text-sm">
                              <XCircle className="w-4 h-4" />
                              Inaktiv
                            </span>
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {userItem.created_at ? new Date(userItem.created_at).toLocaleDateString('de-DE') : '-'}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                          <div className="flex items-center justify-end gap-2">
                            <button
                              onClick={() => {
                                setSelectedUser(userItem);
                                setShowEditModal(true);
                              }}
                              className="text-blue-600 hover:text-blue-900 p-2 hover:bg-blue-50 rounded-lg transition-colors"
                              title="Bearbeiten"
                              data-testid={`edit-user-${userItem._id}`}
                            >
                              <Edit className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => handleResetPassword(userItem._id)}
                              className="text-orange-600 hover:text-orange-900 p-2 hover:bg-orange-50 rounded-lg transition-colors"
                              title="Passwort zurücksetzen"
                              data-testid={`reset-password-${userItem._id}`}
                            >
                              <Key className="w-4 h-4" />
                            </button>
                            {userItem._id !== user.id && (
                              <button
                                onClick={() => handleDeleteUser(userItem._id, userItem.name)}
                                className="text-red-600 hover:text-red-900 p-2 hover:bg-red-50 rounded-lg transition-colors"
                                title="Löschen"
                                data-testid={`delete-user-${userItem._id}`}
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Create User Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-200">
              <h2 className="text-2xl font-bold text-gray-900 flex items-center gap-3">
                <UserPlus className="w-6 h-6 text-blue-600" />
                Neuen Mitarbeiter erstellen
              </h2>
            </div>
            
            <form onSubmit={handleCreateUser} className="p-6 space-y-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Name *</label>
                <input
                  type="text"
                  value={newUser.name}
                  onChange={(e) => setNewUser({...newUser, name: e.target.value})}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="z.B. Max Mustermann"
                  required
                  data-testid="new-user-name"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">E-Mail *</label>
                <input
                  type="email"
                  value={newUser.email}
                  onChange={(e) => setNewUser({...newUser, email: e.target.value})}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="z.B. m.mustermann@firma.de"
                  required
                  data-testid="new-user-email"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Passwort *</label>
                <input
                  type="password"
                  value={newUser.password}
                  onChange={(e) => setNewUser({...newUser, password: e.target.value})}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Mindestens 6 Zeichen"
                  minLength={6}
                  required
                  data-testid="new-user-password"
                />
                <p className="mt-1 text-sm text-gray-500">Das Passwort muss mindestens 6 Zeichen lang sein</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Rolle *</label>
                <div className="space-y-3">
                  {Object.entries(ROLES).map(([roleKey, roleInfo]) => {
                    const RoleIcon = roleInfo.icon;
                    return (
                      <label
                        key={roleKey}
                        className={`flex items-start p-4 border-2 rounded-lg cursor-pointer transition-all ${
                          newUser.role === roleKey
                            ? 'border-blue-500 bg-blue-50'
                            : 'border-gray-200 hover:border-gray-300'
                        }`}
                      >
                        <input
                          type="radio"
                          name="role"
                          value={roleKey}
                          checked={newUser.role === roleKey}
                          onChange={(e) => setNewUser({...newUser, role: e.target.value})}
                          className="mt-1"
                          data-testid={`role-${roleKey}`}
                        />
                        <div className="ml-3 flex-1">
                          <div className="flex items-center gap-2">
                            <RoleIcon className="w-5 h-5 text-gray-600" />
                            <span className="font-semibold text-gray-900">{roleInfo.label}</span>
                          </div>
                          <p className="text-sm text-gray-600 mt-1">{roleInfo.description}</p>
                        </div>
                      </label>
                    );
                  })}
                </div>
              </div>

              <div className="flex items-center justify-end gap-3 pt-4 border-t border-gray-200">
                <button
                  type="button"
                  onClick={() => {
                    setShowCreateModal(false);
                    setNewUser({ name: '', email: '', password: '', role: 'employee' });
                  }}
                  className="px-6 py-3 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors font-medium"
                  data-testid="cancel-create-user"
                >
                  Abbrechen
                </button>
                <button
                  type="submit"
                  className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium"
                  data-testid="submit-create-user"
                >
                  Benutzer erstellen
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit User Modal */}
      {showEditModal && selectedUser && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full">
            <div className="p-6 border-b border-gray-200">
              <h2 className="text-2xl font-bold text-gray-900 flex items-center gap-3">
                <Edit className="w-6 h-6 text-blue-600" />
                Benutzer bearbeiten
              </h2>
            </div>
            
            <div className="p-6 space-y-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Name</label>
                <input
                  type="text"
                  defaultValue={selectedUser.name}
                  onChange={(e) => setSelectedUser({...selectedUser, name: e.target.value})}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  data-testid="edit-user-name"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Rolle</label>
                <div className="space-y-3">
                  {Object.entries(ROLES).map(([roleKey, roleInfo]) => {
                    const RoleIcon = roleInfo.icon;
                    return (
                      <label
                        key={roleKey}
                        className={`flex items-start p-4 border-2 rounded-lg cursor-pointer transition-all ${
                          selectedUser.role === roleKey
                            ? 'border-blue-500 bg-blue-50'
                            : 'border-gray-200 hover:border-gray-300'
                        }`}
                      >
                        <input
                          type="radio"
                          name="edit-role"
                          value={roleKey}
                          checked={selectedUser.role === roleKey}
                          onChange={(e) => setSelectedUser({...selectedUser, role: e.target.value})}
                          className="mt-1"
                          data-testid={`edit-role-${roleKey}`}
                        />
                        <div className="ml-3 flex-1">
                          <div className="flex items-center gap-2">
                            <RoleIcon className="w-5 h-5 text-gray-600" />
                            <span className="font-semibold text-gray-900">{roleInfo.label}</span>
                          </div>
                          <p className="text-sm text-gray-600 mt-1">{roleInfo.description}</p>
                        </div>
                      </label>
                    );
                  })}
                </div>
              </div>

              <div className="flex items-center justify-end gap-3 pt-4 border-t border-gray-200">
                <button
                  type="button"
                  onClick={() => {
                    setShowEditModal(false);
                    setSelectedUser(null);
                  }}
                  className="px-6 py-3 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors font-medium"
                  data-testid="cancel-edit-user"
                >
                  Abbrechen
                </button>
                <button
                  type="button"
                  onClick={() => handleUpdateUser(selectedUser._id, {
                    name: selectedUser.name,
                    role: selectedUser.role
                  })}
                  className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium"
                  data-testid="submit-edit-user"
                >
                  Speichern
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
};

export default UserManagement;
